import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, Divider, Chip, Tooltip, Avatar } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer from "../../MainDrawer"; // Adjust path if needed
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';

// --- Interfaces for Notebook Data ---
interface NotebookOwner {
    id: string;
    name: string;
}

interface CostLabelPair {
    cost: string;
    label: string;
}

interface NotebookDetailsData {
    region: string;
    zone: string;
    workbenchType: string;
    jupyterLabVersion: string;
    machineType: string;
    network: string;
    subNetwork: string;
}

interface NotebookApplication {
    id: string;
    applicationName: string;
}

interface NotebookData {
    id: string;
    name: string;
    cloudProvider: string;
    application: NotebookApplication;
    costLabelPairs: CostLabelPair[];
    status: string;
    details: {
        notebooks: NotebookDetailsData;
    };
    solutionOwner: NotebookOwner;
}

const NotebooksDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [notebook, setNotebook] = useState<NotebookData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchNotebookData = async () => {
            if (!id) {
                setIsLoading(false);
                setError("Notebook ID is missing from URL.");
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetNotebook($getNotebookId: String!) {
                      getNotebook(id: $getNotebookId) {
                        id
                        name
                        cloudProvider
                        application {
                          id
                          applicationName
                        }
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          notebooks {
                            region
                            zone
                            workbenchType
                            jupyterLabVersion
                            machineType
                            network
                            subNetwork
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getNotebookId: id });

                if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching notebook data.");
                    setNotebook(null);
                } else if (response && response.getNotebook) {
                    setNotebook(response.getNotebook);
                } else {
                    setError("Notebook not found or unexpected response structure.");
                    setNotebook(null);
                }
            } catch (err: any) {
                console.error("Error fetching Notebook data:", err);
                setError(err.message || "Failed to fetch Notebook details.");
                setNotebook(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNotebookData();
    }, [id]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading notebook details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!notebook) {
        return <Typography sx={{p:2}}>No notebook data available. Ensure the ID is correct.</Typography>;
    }

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8"; // Default
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved" || lowerStatus === "success") {
            return "#12B76A10"; // Light green tint
        } else if (lowerStatus === "rejected" || lowerStatus === 'failed') {
            return "#F0443810"; // Light red tint
        } else if (lowerStatus === "pending" || lowerStatus === 'provisioning') {
            return "#FDB02210"; // Light yellow/orange tint
        }
        return "#F8F8F8"; // Default for other statuses
    };
    
    const notebookDetails = notebook.details.notebooks;

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/aisolutions/notebooks" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/aisolutions/notebooks'); }}> Notebooks </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Notebook Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/aisolutions/notebooks')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Notebook Details </Typography>
            </Box>

            <Box sx={{mx:2, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{
                    p: 2.5,
                    bgcolor: getStatusBackgroundColor(notebook.status),
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {notebook.name}
                            </Typography>
                            {notebook.status === "pending" && (
                                                                    <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/aisolutions/notebooks/edit/${notebook.id}`)}>
                                                                        <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                                                    </IconButton>
                                                                )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Notebook Owner: {notebook.solutionOwner?.name || 'N/A'}
                        </Typography>
                    </Box>
                </Box>

                {/* --- Main Details Section --- */}
                <Grid container spacing={3} sx={{p:3, pt: 2.5, py: 1, mt:0 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook.cloudProvider?.toUpperCase() || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook.application?.applicationName || "N/A"}</Typography>
                    </Grid>
                     <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Region</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.region}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Zone</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.zone}</Typography>
                    </Grid>
                     <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Workbench Type</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.workbenchType}</Typography>
                    </Grid>
                </Grid>

                {/* --- Environment Section --- */}
                <Box sx={{ mt: 0, p: 2.5, py: 1 }}>
                     <Divider sx={{mb: 2.5}} />
                    <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                        Environment and Networking
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>JupyterLab Version</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.jupyterLabVersion}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Machine Type</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.machineType}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.network}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.subNetwork}</Typography>
                        </Grid>
                    </Grid>
                </Box>
                
                {/* --- Cost Labels Section --- */}
                {notebook.costLabelPairs && notebook.costLabelPairs.length > 0 && (
                    <Box sx={{ mt: 1.5, p: 2.5, py: 1 }}>
                        <Divider sx={{mb: 2.5}} />
                        <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                            Notebook Endpoints
                        </Typography> 
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={5} md={3} mb={2}>
                                                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Endpoint</Typography>
                                                            <Tooltip
                                                                sx={{ whiteSpace: "pre-line" }}
                                                                title={
                                                                    <>
                                                                        <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" fontWeight={700} color="common.white">
                                                                            Notebook Endpoint
                                                                        </Typography>
                                                                        <br />
                                                                        <Link
                                                                            href="https://30d1bcf4ba3505bb-dot-asia-south1.notebooks.googleusercontent.com/lab"
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            sx={{
                                                                                display: 'block',
                                                                                wordBreak: 'break-all',
                                                                                color: '#ffffff',
                                                                                textDecoration: 'none',
                                                                                fontFamily: "Gilroy-Regular",
                                                                                fontSize: "14px",
                                                                                '&:hover': {
                                                                                    textDecoration: 'underline',
                                                                                },
                                                                            }}
                                                                        >
                                                                            url
                                                                        </Link>
                                                                        <br/>
                                                                    </>
                                                                }
                                                                slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                                                            >
                                                                <Avatar
                                                                    src="/assets/info.svg"
                                                                    alt="info"
                                                                    sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

const NotebooksDetailsPage: React.FC = () => {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"aisolutions-notebooks"} />
            <MiniBot/>
            <Box component="main" sx={{ flexGrow: 1, p: 0, bgcolor: '#fafafa' }}>
                <NotebooksDetailsPageContent />
            </Box>
        </Box>
    );
};

export default NotebooksDetailsPage;




// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, Divider, Chip, Tooltip, Avatar } from '@mui/material';
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import MainDrawer from "../../MainDrawer"; // Adjust path if needed
// import queryGraphql from "../../../helpers/ApiInterface";
// import MiniBot from '../../MiniBot';

// // --- Interfaces for Notebook Data ---
// interface NotebookOwner {
//     id: string;
//     name: string;
// }

// interface CostLabelPair {
//     cost: string;
//     label: string;
// }

// interface NotebookDetailsData {
//     region: string;
//     zone: string;
//     workbenchType: string;
//     jupyterLabVersion: string;
//     machineType: string;
//     network: string;
//     subNetwork: string;
// }

// interface NotebookApplication {
//     id: string;
//     applicationName: string;
// }

// interface NotebookData {
//     id: string;
//     name: string;
//     solutionType: string;
//     application: NotebookApplication;
//     costLabelPairs: CostLabelPair[];
//     status: string;
//     details: {
//         notebooks: NotebookDetailsData;
//     };
//     solutionOwner: NotebookOwner;
// }

// const NotebooksDetailsPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams();
//     const [notebook, setNotebook] = useState<NotebookData | null>(null);
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);

//     useEffect(() => {
//         const fetchNotebookData = async () => {
//             if (!id) {
//                 setIsLoading(false);
//                 setError("Notebook ID is missing from URL.");
//                 return;
//             }

//             setIsLoading(true);
//             setError(null);
//             try {
//                 const query = `
//                     query GetNotebook($getNotebookId: String!) {
//                       getNotebook(id: $getNotebookId) {
//                         id
//                         name
//                         solutionType
//                         application {
//                           id
//                           applicationName
//                         }
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         status
//                         details {
//                           notebooks {
//                             region
//                             zone
//                             workbenchType
//                             jupyterLabVersion
//                             machineType
//                             network
//                             subNetwork
//                           }
//                         }
//                         solutionOwner {
//                           id
//                           name
//                         }
//                       }
//                     }
//                 `;
//                 const response = await queryGraphql(query, { getNotebookId: id });

//                 if (response.errors && response.errors.length > 0) {
//                     setError(response.errors[0].message || "Error fetching notebook data.");
//                     setNotebook(null);
//                 } else if (response && response.getNotebook) {
//                     setNotebook(response.getNotebook);
//                 } else {
//                     setError("Notebook not found or unexpected response structure.");
//                     setNotebook(null);
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Notebook data:", err);
//                 setError(err.message || "Failed to fetch Notebook details.");
//                 setNotebook(null);
//             } finally {
//                 setIsLoading(false);
//             }
//         };

//         fetchNotebookData();
//     }, [id]);

//     if (isLoading) {
//         return (
//             <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//                 <CircularProgress />
//                 <Typography sx={{ ml: 2 }}>Loading notebook details...</Typography>
//             </Box>
//         );
//     }

//     if (error) {
//         return <Typography color="error" sx={{p:2}}>{error}</Typography>;
//     }

//     if (!notebook) {
//         return <Typography sx={{p:2}}>No notebook data available. Ensure the ID is correct.</Typography>;
//     }

//     const getStatusBackgroundColor = (status: string | undefined): string => {
//         if (!status) return "#F8F8F8"; // Default
//         const lowerStatus = status.toLowerCase();
//         if (lowerStatus === "active" || lowerStatus === "approved" || lowerStatus === "success") {
//             return "#12B76A10"; // Light green tint
//         } else if (lowerStatus === "rejected" || lowerStatus === 'failed') {
//             return "#F0443810"; // Light red tint
//         } else if (lowerStatus === "pending" || lowerStatus === 'provisioning') {
//             return "#FDB02210"; // Light yellow/orange tint
//         }
//         return "#F8F8F8"; // Default for other statuses
//     };
    
//     const notebookDetails = notebook.details.notebooks;

//     return (
//         <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
//             <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
//                 <Link underline="hover" color="inherit" href="/aisolutions/notebooks" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/aisolutions/notebooks'); }}> Notebooks </Link>
//                 <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Notebook Details</Typography>
//             </Breadcrumbs>

//             <Box display="flex" alignItems="center" mb={3}>
//                 <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/aisolutions/notebooks')}>
//                     <ArrowBackIosNewIcon fontSize="small" />
//                 </IconButton>
//                 <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Notebook Details </Typography>
//             </Box>

//             <Box sx={{mx:2, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
//                 <Box sx={{
//                     p: 2.5,
//                     bgcolor: getStatusBackgroundColor(notebook.status),
//                     borderTopLeftRadius: "8px",
//                     borderTopRightRadius: "8px",
//                 }}>
//                     <Box>
//                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
//                             <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
//                                 {notebook.name}
//                             </Typography>
//                             {notebook.status === "pending" && (
//                                                                     <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/aisolutions/notebooks/edit/${notebook.id}`)}>
//                                                                         <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
//                                                                     </IconButton>
//                                                                 )}
//                         </Box>
//                         <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
//                             Notebook Owner: {notebook.solutionOwner?.name || 'N/A'}
//                         </Typography>
//                     </Box>
//                 </Box>

//                 {/* --- Main Details Section --- */}
//                 <Grid container spacing={3} sx={{p:3, pt: 2.5, py: 1, mt:0 }}>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook.application?.applicationName || "N/A"}</Typography>
//                     </Grid>
//                      <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Region</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.region}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Zone</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.zone}</Typography>
//                     </Grid>
//                      <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Workbench Type</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.workbenchType}</Typography>
//                     </Grid>
//                 </Grid>

//                 {/* --- Environment Section --- */}
//                 <Box sx={{ mt: 0, p: 2.5, py: 1 }}>
//                      <Divider sx={{mb: 2.5}} />
//                     <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                         Environment and Networking
//                     </Typography>
//                     <Grid container spacing={3}>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>JupyterLab Version</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.jupyterLabVersion}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Machine Type</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.machineType}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.network}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.subNetwork}</Typography>
//                         </Grid>
//                     </Grid>
//                 </Box>
                
//                 {/* --- Cost Labels Section --- */}
//                 {notebook.costLabelPairs && notebook.costLabelPairs.length > 0 && (
//                     <Box sx={{ mt: 1.5, p: 2.5, py: 1 }}>
//                         <Divider sx={{mb: 2.5}} />
//                         <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                             Notebook Endpoints
//                         </Typography> 
//                         <Grid container spacing={3}>
//                             <Grid item xs={12} sm={5} md={3} mb={2}>
//                                                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Endpoint</Typography>
//                                                             <Tooltip
//                                                                 sx={{ whiteSpace: "pre-line" }}
//                                                                 title={
//                                                                     <>
//                                                                         <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" fontWeight={700} color="common.white">
//                                                                             Notebook Endpoint
//                                                                         </Typography>
//                                                                         <br />
//                                                                         <Link
//                                                                             href="https://30d1bcf4ba3505bb-dot-asia-south1.notebooks.googleusercontent.com/lab"
//                                                                             target="_blank"
//                                                                             rel="noopener noreferrer"
//                                                                             sx={{
//                                                                                 display: 'block',
//                                                                                 wordBreak: 'break-all',
//                                                                                 color: '#ffffff',
//                                                                                 textDecoration: 'none',
//                                                                                 fontFamily: "Gilroy-Regular",
//                                                                                 fontSize: "14px",
//                                                                                 '&:hover': {
//                                                                                     textDecoration: 'underline',
//                                                                                 },
//                                                                             }}
//                                                                         >
//                                                                             url
//                                                                         </Link>
//                                                                         <br/>
//                                                                     </>
//                                                                 }
//                                                                 slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
//                                                             >
//                                                                 <Avatar
//                                                                     src="/assets/info.svg"
//                                                                     alt="info"
//                                                                     sx={{ height: 16, width: 16, cursor: 'pointer' }}
//                                                                 />
//                                                             </Tooltip>
//                                                         </Grid>
//                         </Grid>
//                     </Box>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// const NotebooksDetailsPage: React.FC = () => {
//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"aisolutions-notebooks"} />
//             <MiniBot/>
//             <Box component="main" sx={{ flexGrow: 1, p: 0, bgcolor: '#fafafa' }}>
//                 <NotebooksDetailsPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default NotebooksDetailsPage;




// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, Divider, Chip, Tooltip, Avatar } from '@mui/material';
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import MainDrawer from "../../MainDrawer"; // Adjust path if needed
// import queryGraphql from "../../../helpers/ApiInterface";
// import MiniBot from '../../MiniBot';

// // --- Interfaces for Notebook Data ---
// interface NotebookOwner {
//     id: string;
//     name: string;
// }

// interface CostLabelPair {
//     cost: string;
//     label: string;
// }

// interface NotebookDetailsData {
//     region: string;
//     zone: string;
//     workbenchType: string;
//     jupyterLabVersion: string;
//     machineType: string;
//     network: string;
//     subNetwork: string;
// }

// interface NotebookData {
//     id: string;
//     name: string;
//     solutionType: string;
//     costLabelPairs: CostLabelPair[];
//     status: string;
//     details: {
//         notebooks: NotebookDetailsData;
//     };
//     solutionOwner: NotebookOwner;
// }

// const NotebooksDetailsPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams();
//     const [notebook, setNotebook] = useState<NotebookData | null>(null);
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);

//     useEffect(() => {
//         const fetchNotebookData = async () => {
//             if (!id) {
//                 setIsLoading(false);
//                 setError("Notebook ID is missing from URL.");
//                 return;
//             }

//             setIsLoading(true);
//             setError(null);
//             try {
//                 const query = `
//                     query GetNotebook($getNotebookId: String!) {
//                       getNotebook(id: $getNotebookId) {
//                         id
//                         name
//                         solutionType
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         status
//                         details {
//                           notebooks {
//                             region
//                             zone
//                             workbenchType
//                             jupyterLabVersion
//                             machineType
//                             network
//                             subNetwork
//                           }
//                         }
//                         solutionOwner {
//                           id
//                           name
//                         }
//                       }
//                     }
//                 `;
//                 const response = await queryGraphql(query, { getNotebookId: id });

//                 if (response.errors && response.errors.length > 0) {
//                     setError(response.errors[0].message || "Error fetching notebook data.");
//                     setNotebook(null);
//                 } else if (response && response.getNotebook) {
//                     setNotebook(response.getNotebook);
//                 } else {
//                     setError("Notebook not found or unexpected response structure.");
//                     setNotebook(null);
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Notebook data:", err);
//                 setError(err.message || "Failed to fetch Notebook details.");
//                 setNotebook(null);
//             } finally {
//                 setIsLoading(false);
//             }
//         };

//         fetchNotebookData();
//     }, [id]);

//     if (isLoading) {
//         return (
//             <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//                 <CircularProgress />
//                 <Typography sx={{ ml: 2 }}>Loading notebook details...</Typography>
//             </Box>
//         );
//     }

//     if (error) {
//         return <Typography color="error" sx={{p:2}}>{error}</Typography>;
//     }

//     if (!notebook) {
//         return <Typography sx={{p:2}}>No notebook data available. Ensure the ID is correct.</Typography>;
//     }

//     const getStatusBackgroundColor = (status: string | undefined): string => {
//         if (!status) return "#F8F8F8"; // Default
//         const lowerStatus = status.toLowerCase();
//         if (lowerStatus === "active" || lowerStatus === "approved" || lowerStatus === "success") {
//             return "#12B76A10"; // Light green tint
//         } else if (lowerStatus === "rejected" || lowerStatus === 'failed') {
//             return "#F0443810"; // Light red tint
//         } else if (lowerStatus === "pending" || lowerStatus === 'provisioning') {
//             return "#FDB02210"; // Light yellow/orange tint
//         }
//         return "#F8F8F8"; // Default for other statuses
//     };
    
//     const notebookDetails = notebook.details.notebooks;

//     return (
//         <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
//             <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
//                 <Link underline="hover" color="inherit" href="/aisolutions/notebooks" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/aisolutions/notebooks'); }}> Notebooks </Link>
//                 <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Notebook Details</Typography>
//             </Breadcrumbs>

//             <Box display="flex" alignItems="center" mb={3}>
//                 <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/aisolutions/notebooks')}>
//                     <ArrowBackIosNewIcon fontSize="small" />
//                 </IconButton>
//                 <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Notebook Details </Typography>
//             </Box>

//             <Box sx={{mx:2, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
//                 <Box sx={{
//                     p: 2.5,
//                     bgcolor: getStatusBackgroundColor(notebook.status),
//                     borderTopLeftRadius: "8px",
//                     borderTopRightRadius: "8px",
//                 }}>
//                     <Box>
//                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
//                             <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
//                                 {notebook.name}
//                             </Typography>
//                             {notebook.status === "pending" && (
//                                                                     <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/aisolutions/notebooks/edit/${notebook.id}`)}>
//                                                                         <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
//                                                                     </IconButton>
//                                                                 )}
//                         </Box>
//                         <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
//                             Notebook Owner: {notebook.solutionOwner?.name || 'N/A'}
//                         </Typography>
//                     </Box>
//                 </Box>

//                 {/* --- Main Details Section --- */}
//                 <Grid container spacing={3} sx={{p:3, pt: 2.5, py: 1, mt:0 }}>
//                     {/* <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Solution Type</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook.solutionType}</Typography>
//                     </Grid> */}
//                      <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Region</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.region}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Zone</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.zone}</Typography>
//                     </Grid>
//                      <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Workbench Type</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.workbenchType}</Typography>
//                     </Grid>
//                 </Grid>

//                 {/* --- Environment Section --- */}
//                 <Box sx={{ mt: 0, p: 2.5, py: 1 }}>
//                      <Divider sx={{mb: 2.5}} />
//                     <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                         Environment and Networking
//                     </Typography>
//                     <Grid container spacing={3}>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>JupyterLab Version</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.jupyterLabVersion}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Machine Type</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.machineType}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.network}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.subNetwork}</Typography>
//                         </Grid>
//                     </Grid>
//                 </Box>
                
//                 {/* --- Networking Section --- */}
//                 {/* <Box sx={{ mt: 1.5, p: 2.5, py: 1}}>
//                      <Divider sx={{mb: 2.5}} />
//                     <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                         Networking
//                     </Typography>
//                     <Grid container spacing={3}>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.network}</Typography>
//                         </Grid>
//                         <Grid item xs={6} sm={3}>
//                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
//                             <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebookDetails.subNetwork}</Typography>
//                         </Grid>
//                     </Grid>
//                 </Box> */}

//                 {/* --- Cost Labels Section --- */}
//                 {notebook.costLabelPairs && notebook.costLabelPairs.length > 0 && (
//                     <Box sx={{ mt: 1.5, p: 2.5, py: 1 }}>
//                         <Divider sx={{mb: 2.5}} />
//                         {/* <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                             Cost Allocation Labels
//                         </Typography> */}
//                         <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                             Notebook Endpoints
//                         </Typography> 
//                         <Grid container spacing={3}>
//                             {/* {notebook.costLabelPairs.map((pair, index) => (
//                                 <>
//                                 <Grid item xs={12} sm={4} md={3} key={index}>
//                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666', textTransform: 'capitalize' }}>{pair.cost.replace(/_/g, ' ')}</Typography>
//                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{pair.label}</Typography>
//                                 </Grid>
                                
//                                                         </>
//                             ))} */}

//                             <Grid item xs={12} sm={5} md={3} mb={2}>
//                                                             <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Endpoint</Typography>
//                                                             <Tooltip
//                                                                 sx={{ whiteSpace: "pre-line" }}
//                                                                 title={
//                                                                     <>
//                                                                         <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" fontWeight={700} color="common.white">
//                                                                             Notebook Endpoint
//                                                                         </Typography>
//                                                                         <br />
//                                                                         <Link
//                                                                             href="https://30d1bcf4ba3505bb-dot-asia-south1.notebooks.googleusercontent.com/lab"
//                                                                             target="_blank"
//                                                                             rel="noopener noreferrer"
//                                                                             sx={{
//                                                                                 display: 'block',
//                                                                                 wordBreak: 'break-all',
//                                                                                 color: '#ffffff',
//                                                                                 textDecoration: 'none',
//                                                                                 fontFamily: "Gilroy-Regular",
//                                                                                 fontSize: "14px",
//                                                                                 '&:hover': {
//                                                                                     textDecoration: 'underline',
//                                                                                 },
//                                                                             }}
//                                                                         >
//                                                                             url
//                                                                         </Link>
//                                                                         <br/>
//                                                                     </>
//                                                                 }
//                                                                 slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
//                                                             >
//                                                                 <Avatar
//                                                                     src="/assets/info.svg"
//                                                                     alt="info"
//                                                                     sx={{ height: 16, width: 16, cursor: 'pointer' }}
//                                                                 />
//                                                             </Tooltip>
//                                                         </Grid>
//                         </Grid>
//                     </Box>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// const NotebooksDetailsPage: React.FC = () => {
//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"aisolutions-notebooks"} />
//             <MiniBot/>
//             <Box component="main" sx={{ flexGrow: 1, p: 0, bgcolor: '#fafafa' }}>
//                 <NotebooksDetailsPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default NotebooksDetailsPage;